import { AnimatePresence, m } from 'motion/react';
import type { ReactNode } from 'react';

import { useT } from '../../i18n/provider';

/**
 * El armazón de la barra lateral: fija en escritorio, cajón deslizante por
 * debajo de `lg`.
 *
 * Es el comportamiento que `13 §7` pide para el rango de 768 a 1279 píxeles, y
 * el código es el que ya vivía dentro de `AppShell`. Sale de allí porque ahora
 * hay dos barras —la global y la de una empresa— y el armazón es lo único que
 * comparten: quién se pinta dentro lo decide el marco.
 *
 * El contenido llega como hijo y no como propiedad de configuración porque las
 * dos barras no se parecen en nada salvo en el hueco que ocupan.
 */
export function Sidebar({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const t = useT();

  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-sidebar lg:block">{children}</aside>

      <AnimatePresence>
        {open ? (
          <>
            <m.button
              type="button"
              aria-label={t('nav.closeMenu')}
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/55 backdrop-blur-xs lg:hidden"
            />
            <m.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 420, damping: 40 }}
              className="fixed inset-y-0 left-0 z-50 w-sidebar lg:hidden"
            >
              {children}
            </m.aside>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}

/**
 * El interior de cualquiera de las dos barras.
 *
 * La columna es `flex` con `min-h-0` a propósito: es lo que permite que la zona
 * de enlaces crezca y desplace sin empujar al pie fuera de la pantalla. Con
 * veintiuna secciones eso deja de ser un detalle.
 */
export function SidebarBody({ children }: { children: ReactNode }) {
  return <div className="flex h-full min-h-0 flex-col bg-nav text-nav-ink">{children}</div>;
}
